import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FiImage, FiUpload, FiCheck, FiClock, FiAlertCircle, FiSearch,
  FiRefreshCw, FiEye, FiUser, FiCalendar, FiChevronRight, FiMail,
  FiSend, FiRepeat
} from 'react-icons/fi';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const API_URL = getApiUrl();

export default function DesignsListPage() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter reservations that are confirmed and need design validation
        const designReservations = data.filter(r => 
          r.status === 'confirmed' || r.status === 'approved' || r.design_status
        );
        setReservations(designReservations);
      }
    } catch (error) {
      console.error('Error loading reservations:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const sendDesignReminder = async (e, reservation) => {
    e.stopPropagation();
    setSendingReminder(reservation.id);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/designs/${reservation.id}/send-reminder`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(`Relance envoyée à ${reservation.client_name}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la relance');
    } finally {
      setSendingReminder(null);
    }
  };

  const getDesignStatusBadge = (reservation) => {
    const status = reservation.design_status;
    if (status === 'approved') {
      return <Badge className="bg-green-100 text-green-700">Design validé</Badge>;
    }
    if (status === 'revision_requested') {
      return <Badge className="bg-orange-100 text-orange-700">Révision demandée</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-blue-100 text-blue-700">En attente validation</Badge>;
    }
    if (reservation.design_url) {
      return <Badge className="bg-amber-100 text-amber-700">Design envoyé</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Pas de design</Badge>;
  };

  const getVersionCount = (reservation) => {
    // Count versions from design_messages or design_history
    const messages = reservation.design_messages || [];
    const versions = messages.filter(m => m.type === 'design_upload' || m.has_design).length;
    return versions || reservation.design_version || 0;
  };

  const filteredReservations = reservations.filter(r => {
    const matchesSearch = 
      r.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.event_type?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTab = 
      activeTab === 'all' ||
      (activeTab === 'pending' && (!r.design_status || r.design_status === 'pending')) ||
      (activeTab === 'revision' && r.design_status === 'revision_requested') ||
      (activeTab === 'approved' && r.design_status === 'approved');
    
    return matchesSearch && matchesTab;
  });

  const pendingCount = reservations.filter(r => !r.design_status || r.design_status === 'pending').length;
  const revisionCount = reservations.filter(r => r.design_status === 'revision_requested').length;
  const approvedCount = reservations.filter(r => r.design_status === 'approved').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="designs-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Validation des Designs</h1>
          <p className="text-slate-500">Gérez les designs et maquettes pour vos clients</p>
        </div>
        <Button onClick={loadReservations} variant="outline" className="gap-2">
          <FiRefreshCw className="w-4 h-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FiImage className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reservations.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiClock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                <p className="text-sm text-amber-600">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FiAlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{revisionCount}</p>
                <p className="text-sm text-orange-600">Révisions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{approvedCount}</p>
                <p className="text-sm text-green-600">Validés</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Search */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <FiClock className="w-4 h-4" />
              En attente ({pendingCount})
            </TabsTrigger>
            <TabsTrigger value="revision" className="gap-2">
              <FiAlertCircle className="w-4 h-4" />
              Révisions ({revisionCount})
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <FiCheck className="w-4 h-4" />
              Validés ({approvedCount})
            </TabsTrigger>
            <TabsTrigger value="all">Tous</TabsTrigger>
          </TabsList>
          
          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="design-search-input"
            />
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-6">
          {filteredReservations.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FiImage className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune réservation</h3>
                <p className="text-slate-500">
                  {searchQuery ? 'Aucun résultat pour cette recherche' : 'Pas de design à valider pour le moment'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredReservations.map((reservation) => {
                const versionCount = getVersionCount(reservation);
                return (
                  <motion.div
                    key={reservation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`design-item-${reservation.id}`}
                  >
                    <Card className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/dashboard/design/${reservation.id}`)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="p-3 bg-indigo-100 rounded-lg">
                              <FiImage className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-slate-900">{reservation.client_name}</h3>
                                {getDesignStatusBadge(reservation)}
                                {versionCount > 0 && (
                                  <Badge variant="outline" className="gap-1" data-testid="version-badge">
                                    <FiRepeat className="w-3 h-3" />
                                    V{versionCount}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-600">{reservation.event_type}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <FiCalendar className="w-4 h-4" />
                                  {formatDate(reservation.event_date || reservation.start_date)}
                                </span>
                                {reservation.design_messages?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <FiImage className="w-4 h-4" />
                                    {reservation.design_messages.length} message(s)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Bouton de relance - visible uniquement si en attente de validation client */}
                            {(reservation.design_status === 'pending' || 
                              (reservation.design_url && !reservation.design_status)) && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={(e) => sendDesignReminder(e, reservation)}
                                disabled={sendingReminder === reservation.id}
                                data-testid="send-reminder-btn"
                              >
                                {sendingReminder === reservation.id ? (
                                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FiSend className="w-4 h-4" />
                                )}
                                Relancer
                              </Button>
                            )}
                            <Button variant="outline" size="sm" className="gap-1">
                              <FiEye className="w-4 h-4" />
                              Voir
                            </Button>
                            {!reservation.design_status && !reservation.design_url && (
                              <Button size="sm" className="gap-1">
                                <FiUpload className="w-4 h-4" />
                                Envoyer design
                              </Button>
                            )}
                            <FiChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
